/**
 * Logotipos de pasarela que se sirven desde el propio checkout.
 *
 * <p>Son activos de marca de cada proveedor, tomados de su paquete OFICIAL para integradores
 * —Webpay Plus viene de `transbankdevelopers.cl/brand-assets`— y usados tal cual, sin recolorear ni
 * deformar, que es la condicion que ponen sus manuales de marca. Se sirven desde aqui y no desde el
 * sitio del proveedor para no depender de un tercero en la ruta critica del pago ni filtrarle que
 * un comprador concreto esta en esta pantalla.
 *
 * <p>Manda siempre lo que configure el tenant en `public_config.logo_url`; esto es el respaldo para
 * las pasarelas que la plataforma ya conoce. Una pasarela sin entrada aqui ni logo configurado cae
 * en el monograma de {@link BrandMark}, que es un final digno y no un hueco.
 *
 * <p><strong>De donde salen, exactamente</strong>, para que sean reproducibles y para que nadie los
 * rehaga a mano:
 *
 * <ul>
 *   <li>webpay: `logo-webpay-plus.zip`, variante de fondo blanco a 300px. El archivo del repositorio
 *       mide 285x72 frente al oficial de 301x76, pero conserva su ratio (3.96), asi que es el mismo
 *       asset reescalado y NO se vuelve a tocar.
 *   <li>oneclick: `logo-webpay-oneclick.zip`, ruta
 *       `PNG_WebpayOneclick/_FondoBlanco_PNG/_300px/1.WebpayOneclick_FB_300px.png`, 301x78, tal cual
 *       sale del paquete (md5 eb583f8a20e835b0def6100f587d6b2d).
 *   <li>getnet: `getnet_logo.svg` de <https://banco.santander.cl>, descargado el 2026-08-24. 100x25,
 *       3.051 bytes, md5 0bc50dcb3032c1ea88b8d8ef30b14062. Es el sitio de SANTANDER CHILE, que es
 *       quien opera Getnet en el pais, asi que la procedencia es la del titular de la marca y no
 *       una copia de terceros. Se sirve tal cual, sin recolorear ni reescalar.
 *   <li>amipass: `logo.png` del bucket `brand-assets/amipass/` de amipass.com, descargado el
 *       2026-08-24. 553x132, 14.066 bytes, md5 54f1ed1783c863bd18e29a7a9405cbe4. Es el almacenamiento
 *       de la propia Amipass --la ruta se llama literalmente `brand-assets`--, asi que la
 *       procedencia es la del titular. Se tomo el ORIGINAL y no la URL de
 *       `www.amipass.com/_next/image?...&w=128&q=75` que lleva a el: esa es el optimizador de
 *       imagenes de su Next.js y devuelve un derivado de 128x31 recomprimido. Empaquetar el derivado
 *       seria dejar que el tamano del logo lo decidiera el `w=` de un enlace copiado.
 *   <li>mach: `MACH+logo.png` del bucket de documentacion de su propia API
 *       (`342690394898-documentation.s3.amazonaws.com/apis/mach-api/`), descargado el 2026-08-25.
 *       El original media 1094x434 RGBA (7.206 bytes, md5 0ee5d59d1206d4cb567e0ab2ac330f4f) y aqui
 *       se guarda RECORTADO a 428x90 (3.300 bytes, md5 a3d30618c99a4da125eb9edad59bccd3).
 *       <strong>Es el unico logo que se ha tocado, y el motivo se midio:</strong> la marca visible
 *       ocupaba solo el <strong>21 % del alto</strong> de su lienzo --el otro 79 % era margen
 *       transparente-- y `object-contain` encaja el lienzo COMPLETO, margen incluido, asi que en el
 *       selector se veia diminuta al lado de las demas. Recortar el vacio no recolorea ni deforma
 *       nada: la marca conserva sus proporciones exactas, solo pierde aire que no era suyo.
 *       Comparativa del alto que cada logo ancho aprovecha frente al `maxWidth: size * 3` de
 *       BrandMark: getnet 75 %, amipass 71 %, mach 63 % --y 21 % antes del recorte--.
 *       <strong>Trae alfa de verdad</strong> --color type 6, sin `tRNS`-- asi que al contrario que
 *       el logo del hub no necesita pastilla blanca detras: se apoya en el fondo que haya.
 *   <li>compraaqui: `Logo-Compraqui (1).png` de Wikimedia Commons, descargado el 2026-08-27. El
 *       original mide 1836x577 RGBA (43.785 bytes, md5 3cfffad3fe93b0da3a1509fc300e4199) y aqui se
 *       guarda recortado y reescalado a 440x123 (9.362 bytes, md5
 *       fcbf5cd978547b98e5e89e731f5b7606).
 *       <p><strong>Su procedencia NO es como la de los demas, y conviene no disimularlo</strong>:
 *       los otros cinco salen del sitio del titular de la marca --Transbank, Santander, Amipass, la
 *       documentacion de la propia MACH--. Este sale de una subida de un particular a Commons
 *       (usuario `Pacarcamo`, declarada «trabajo propio», licencia CC BY-SA 4.0, categorizada como
 *       «Unidentified logos»). Quien sube a Commons no puede licenciar una marca ajena: el
 *       logotipo de CompraAqui es de Banco Estado, se use aqui bajo la licencia que se use. Se
 *       empaqueta porque identificar el medio de pago con su marca es exactamente para lo que
 *       existe --el mismo uso que los otros cinco-- pero si algun dia hay que sustituirlo por el
 *       asset oficial del banco, esta entrada es la primera candidata, y quitarla no rompe nada:
 *       el checkout vuelve al monograma de BrandMark.
 *       <p>Se siguio la regla de abajo: la URL que llego era un `thumb` de 960px de Wikimedia
 *       --un derivado reescalado por un tercero-- y se fue al original de `upload.wikimedia.org`
 *       para empaquetar un reescalado NUESTRO. El recorte del margen transparente es el mismo
 *       tratamiento que MACH y por el mismo motivo. La cuantizacion a 256 colores tampoco es
 *       cosmetica: el reescalado en RGBA pesaba 41.843 bytes --el triple que el logo mas pesado del
 *       widget-- y la imagen solo tiene 637 colores distintos, asi que la paleta la reproduce con
 *       una diferencia maxima de 27/255, media de 0,51/255 y CERO pixeles con el alfa volteado.
 *
 *       <p>SE DESCARTO la alternativa de Wikimedia Commons
 *       (`Logotipo_MACH.svg/960px-...png`), y no por purismo: medida igual, su marca da ratio 4.46
 *       y aprovecharia el 67 % del alto --cuatro puntos sobre la oficial, imperceptible-- a cambio
 *       de pesar <strong>diez veces mas</strong> (32.842 bytes) y de venir de un TERCERO en vez del
 *       titular. El problema nunca fue el asset: era el margen.
 *   <li>rutpay: `logo-rutpay.png` del fragmento de cabecera del sitio de BancoEstado
 *       (`/content/experience-fragments/bancoestado-public/.../header-rutpay/...`), descargado el
 *       2026-09-02. 158x72 RGBA, 3.700 bytes, md5 b7c5c8b2e71e25972d9518ced664110b. Se toma tal
 *       cual: <strong>aprovecha el 100 % de su lienzo</strong> --se midio, y no tiene ni un pixel de
 *       margen transparente-- asi que no hay nada que recortar, al reves que en MACH.
 *       <p><strong>NO es la variante que primero aparece buscando</strong>, y la diferencia importa:
 *       la de la portada de RutPay es `rutpay-bancoestado-blanco.png`, con «RUT» en BLANCO sobre
 *       transparente. Sobre el fondo claro del checkout esa se pierde entera y solo se leeria
 *       «pay». Esta trae el texto oscuro y la bajada «de BancoEstado», que es la que funciona aqui.
 *       <p>Su ratio es 2.19, el mas bajo del conjunto --los demas van de 3.5 a 4.8-- porque es un
 *       logotipo de DOS lineas. No es un problema: `BrandMark` fija el alto y deja correr el ancho
 *       hasta un tope, asi que uno mas cuadrado simplemente no llega a ese tope.
 *   <li>bci: `gateway-bci.png`, anadido el 2026-09-16 con el encendido de la pasarela. 480x145,
 *       10.922 bytes, md5 d4f49ba8de7abbad5922400c65080f65. Ratio 3.31, o sea el del grupo.
 *       <p><strong>NO trae transparencia, y ES EL UNICO: es una pieza de FONDO OSCURO.</strong> Se
 *       midio pixel a pixel como los demas y el resultado no admite interpretacion: los 69.600
 *       pixeles llevan alfa 255 --ni uno transparente-- y el <strong>82,9 %</strong> del lienzo es
 *       negro opaco (#000000 el 72,8 %, #1e1e1e el 8,1 %); el azul de la marca, #005fff, ocupa el
 *       13,2 %. Los otros siete van del 63 % al 73 % de pixeles transparentes.
 *       <p>Consecuencia visible: `BrandMark` lo pinta con `object-contain` sobre la superficie del
 *       checkout, que es blanca (`SURFACE_CLASS`), asi que en la fila del selector se ve un
 *       RECTANGULO NEGRO de 108x33 px al lado de siete logos que se apoyan en el fondo. Aprovecha el
 *       91 % del alto de su caja --el mejor del conjunto, por delante de getnet (75 %) y mach
 *       (63 %)--, asi que el tamano no es el problema: lo es el fondo.
 *       <p><strong>No se toca el fichero.</strong> Recortarlo no arregla nada (el fondo no es
 *       margen, es la pieza) y repintarlo seria recolorear una marca ajena, que es justo lo que los
 *       manuales prohiben y lo que este archivo lleva evitando desde el primer logo. Lo que hace
 *       falta es la VARIANTE PARA FONDO CLARO del paquete de marca de BCI, que es la misma leccion
 *       que dejo RutPay --alli la trampa era la contraria: la variante de «RUT» en blanco se perdia
 *       sobre el fondo claro--. Mientras tanto se empaqueta esta, porque identifica la marca y el
 *       monograma «BC» no; el dia que llegue la buena, sustituir el fichero es todo el trabajo.
 *       <p>Y su procedencia NO esta apuntada, al contrario que las siete de arriba: el fichero
 *       llego al arbol sin registro de donde salio. Queda dicho aqui en vez de inventado.
 *   <li>santander: `gateway-santander.svg`, anadido el 2026-10-29. Sale del logo que publica el
 *       propio banco en banco.santander.cl (`logo_santander_new.svg`, 3.494 bytes), revisado antes
 *       de entrar: sin scripts ni referencias externas. Dos cambios, y solo dos: el trazo pasa del
 *       BLANCO original (es la version para su cabecera roja, invisible sobre nuestro fondo) al rojo
 *       corporativo #EC0000, y se le anade `viewBox="0 0 264 50"` --sin el, un SVG de tamano fijo
 *       se RECORTA en vez de escalar dentro de `BrandMark`--. 3.518 bytes, md5
 *       4d75ff10d5a0f278a95b4b53bc87e6e1. Ratio 5.28.
 * </ul>
 *
 * <p>Se sirven en PNG y no en WebP: pesan 5 KB, y el ahorro de 2 KB no compensa mantener dos
 * codificaciones del mismo archivo ni depender de una herramienta de conversion que no esta en la
 * maquina de desarrollo. `BrandMark` los pinta con `object-contain` y `maxWidth`, asi que se ven a
 * ~28x108 SIN deformarse, que es la condicion que ponen los manuales de marca de Transbank.
 *
 * <p><strong>Aviso de licencia, no legal pero si operativo</strong>: la pagina de brand assets de
 * Transbank dice que se prohibe el uso o la reproduccion de sus marcas «sin la autorizacion previa,
 * expresa y por escrito de Transbank». Quien opera esta plataforma es integrador de Transbank y tiene
 * la relacion comercial; si esa autorizacion no existiera, lo que hay que quitar son estas dos
 * entradas, y el checkout seguiria funcionando con el monograma de BrandMark.
 *
 * <p><strong>Getnet es el unico SVG, y es un hecho del proveedor, no una preferencia</strong>: asi
 * lo publica su titular. Convertirlo a PNG seria rasterizar a mano un vector que ya viene bien y
 * elegir nosotros una resolucion. El fichero se inspecciono antes de entrar --sin `<script>`, sin
 * `onload`, sin `foreignObject` y sin una sola referencia externa: solo `<path>`-- y ademas
 * `BrandMark` lo pinta con `<img>`, contexto en el que un SVG no ejecuta nada aunque lo trajera.
 */
const BUNDLED_LOGOS: Readonly<Record<string, string>> = {
  webpay: '/gateway-webpay.webp',
  oneclick: '/gateway-oneclick.png',
  getnet: '/gateway-getnet.svg',
  amipass: '/gateway-amipass.png',
  mach: '/gateway-mach.png',
  compraaqui: '/gateway-compraaqui.png',
  rutpay: '/gateway-rutpay.png',
  bci: '/gateway-bci.png',
  santander: '/gateway-santander.svg',
};

/*
 * LOS TRES ATAJOS QUE NO SE TOMAN, y que siguen sin tomarse aunque los ficheros de Getnet y de
 * Amipass ya existan. Se dejan escritos porque la proxima pasarela sin logo va a plantear lo mismo:
 *
 *   - APUNTAR A LA URL DEL LOGO EN EL SITIO DEL PROVEEDOR. Rompe las dos razones por las que estos
 *     ficheros se sirven desde aqui: mete a un tercero en la ruta critica del pago --si su CDN esta
 *     caido, la fila del medio de pago se queda sin marca-- y le cuenta que un comprador concreto
 *     esta mirando esta pantalla ahora mismo. Que la URL exista NO es motivo para enlazarla: se
 *     descarga, se inspecciona y se empaqueta, que es lo que se hizo con Getnet y con Amipass.
 *   - ENLAZAR --O DESCARGAR-- LA URL DE UN OPTIMIZADOR DE IMAGENES. Es el caso de Amipass: su web
 *     sirve el logo a traves de `/_next/image?url=...&w=128&q=75`, que devuelve un derivado
 *     reescalado y recomprimido. Copiar ese enlace deja que el tamano del logo lo decida el `w=` que
 *     traia; hay que seguirlo hasta el original y empaquetar ESE.
 *   - DIBUJAR ALGO PARECIDO. Un logotipo aproximado de una marca ajena es peor que no ponerlo:
 *     ademas de ser una infraccion, al comprador le da una pista falsa sobre a quien le esta
 *     entregando su tarjeta.
 *
 * Una pasarela sin entrada aqui se pinta con el monograma de {@link BrandMark}, que es el final
 * digno previsto y el mismo que ve hoy la pasarela de pruebas. Hay un test que lo fija.
 */

/** Logo propio de la pasarela, si la plataforma lo trae empaquetado. */
export function bundledGatewayLogo(gatewayId: string): string | undefined {
  return BUNDLED_LOGOS[gatewayId.toLowerCase()];
}

/**
 * El boton de pago CON LA MARCA de la pasarela: su color y, en blanco, su simbolo.
 *
 * <p>Solo para el boton de la pantalla de la propia pasarela, nunca en el selector: ahi las filas
 * van neutras y el color lo pone solo cada logo, porque varios colores de marca compitiendo en la
 * misma lista se leen como ruido (decidido con el usuario el 2026-10-29). Fuera del boton manda el
 * tema del comercio.
 *
 * <p>Una pasarela entra aqui solo con su color OFICIAL medido, no a ojo. Santander: #EC0000 es el
 * del logo que publica el propio banco, y el blanco sobre el da 4,6:1 (AA). El simbolo es la llama
 * de ese mismo SVG, recortada por `viewBox` --el trazo no se toca--: en blanco, porque el logo rojo
 * sobre el boton rojo desapareceria.
 */
export interface GatewayButtonBrand {
  color: string;
  mark: string;
}

const BUTTON_BRANDS: Readonly<Record<string, GatewayButtonBrand>> = {
  santander: { color: '#EC0000', mark: '/gateway-santander-llama.svg' },
};

export function gatewayButtonBrand(gatewayId: string): GatewayButtonBrand | undefined {
  return BUTTON_BRANDS[gatewayId.toLowerCase()];
}
